<template>
  <el-dialog
      :model-value="modelValue"
      :title="isEdit ? 'Davriylikni tahrirlash' : 'Yangi davriylik'"
      width="580px"
      align-center
      destroy-on-close
      @update:model-value="$emit('update:modelValue', $event)"
      @closed="langErrors = []"
  >
    <!-- Portalda qanday ko'rinishining namunasi -->
    <div class="fc-preview">
      <span class="fc-preview__dot" :style="{background: form.color || defaultColor}"/>
      <span class="fc-preview__title">{{ previewTitle }}</span>
      <span
          class="fc-preview__badge"
          :style="{background: form.color || defaultColor, color: badgeTextColor}"
      >15</span>
      <span v-if="!form.is_active" class="fc-preview__off">Nofaol</span>
    </div>

    <el-form ref="formRef" :model="form" :rules="rules" label-position="top">
      <div class="fc-label">Nomi <span class="fc-label__hint">(kamida o'zbekcha lotin)</span></div>
      <el-tabs v-model="activeLang" class="fc-lang-tabs">
        <el-tab-pane v-for="lt in langTabs" :key="lt.code" :name="lt.code">
          <template #label>
            <span class="fc-lang-tab" :class="{'is-error': langErrors.includes(lt.code)}">
              {{ lt.tabLabel }}
              <span
                  class="fc-lang-tab__dot"
                  :class="{'is-filled': !!form.title[lt.code]}"
                  :title="form.title[lt.code] ? 'To\'ldirilgan' : 'Bo\'sh'"
              />
            </span>
          </template>
          <el-form-item :prop="'title.' + lt.code" label-width="0">
            <el-input
                v-model="form.title[lt.code]"
                :placeholder="'Davriylik nomi — ' + langLabels[lt.code]"
                clearable
                @input="clearLangError(lt.code)"
            />
          </el-form-item>
        </el-tab-pane>
      </el-tabs>

      <el-form-item label="Kod" prop="code">
        <el-input
            v-model="form.code"
            placeholder="monthly"
            :maxlength="codeMaxLength"
            show-word-limit
            @input="form.code = form.code.toLowerCase()"
        />
        <div class="fc-hint">
          Faqat kichik lotin harflar, raqam, <code>_</code> va <code>-</code>.
          Portal filtri shu kod bilan ishlaydi: <code>?periodicity={{ form.code || "monthly" }}</code>
        </div>
        <el-alert
            v-if="isEdit && form.code !== originalCode"
            type="warning"
            :closable="false"
            show-icon
            class="fc-alert"
        >
          Kod o'zgartirilmoqda. Portalda saqlangan filtr havolalari
          (<code>?periodicity={{ originalCode }}</code>) ishlamay qoladi.
        </el-alert>
      </el-form-item>

      <div class="fc-label">Rang</div>
      <div class="fc-swatches">
        <button
            v-for="c in colorPalette"
            :key="c"
            type="button"
            class="fc-swatch"
            :class="{'is-active': isSelectedColor(c)}"
            :style="{background: c}"
            :title="c"
            @click="form.color = c"
        >
          <span v-if="isSelectedColor(c)" class="fc-swatch__check">✓</span>
        </button>
        <el-color-picker v-model="form.color" :predefine="colorPalette"/>
        <span class="fc-swatch-hex">{{ form.color || defaultColor }}</span>
      </div>

      <el-row :gutter="16">
        <el-col :span="12">
          <div class="fc-label">Tartib raqami</div>
          <el-input-number v-model="form.order_number" controls-position="right" style="width: 100%"/>
          <div class="fc-hint">
            Legend tartibi. Yangi davriylikka oxirgi raqamdan keyingisi qo'yiladi.
          </div>
        </el-col>
        <el-col :span="12">
          <div class="fc-label">Holati</div>
          <el-switch
              v-model="form.is_active"
              inline-prompt
              active-text="Faol"
              inactive-text="Nofaol"
              style="--el-switch-on-color: #16A34A"
          />
          <div class="fc-hint">
            Nofaol qilinsa legend'dan chiqadi va bu davriylikdagi
            <b>hamma badge portaldan yo'qoladi</b>.
          </div>
        </el-col>
      </el-row>
    </el-form>

    <template #footer>
      <el-button plain :disabled="submitting" @click="$emit('update:modelValue', false)">
        Bekor qilish
      </el-button>
      <el-button type="primary" :loading="submitting" @click="onSubmit">
        {{ isEdit ? "Saqlash" : "Qo'shish" }}
      </el-button>
    </template>
  </el-dialog>
</template>

<script>
import { ElMessage, ElNotification } from "element-plus";
import api, {
  LANG_TABS,
  LANG_LABELS,
  fillMultiLang,
  displayLangText,
  COLOR_PALETTE,
  DEFAULT_COLOR,
  CODE_PATTERN,
  CODE_MAX_LENGTH,
  normalizeHex,
  contrastTextColor,
  apiErrorMessage,
} from "./gfsCalendarApi";

export default {
  name: "GfsPeriodicityDialog",
  props: {
    modelValue: { type: Boolean, default: false },
    // null = yaratish, aks holda tahrirlanayotgan davriylik.
    periodicity: { type: Object, default: null },
    // Yangi davriylikka palitradan navbatdagi rangni berish uchun.
    count: { type: Number, default: 0 },
    // Yangi davriylik uchun taklif qilinadigan tartib raqami (ro'yxatdagi oxirgisi + 1).
    nextOrder: { type: Number, default: 1 },
  },
  emits: ["update:modelValue", "saved"],
  data() {
    return {
      submitting: false,
      activeLang: "uz_lat",
      langErrors: [],
      langTabs: LANG_TABS,
      langLabels: LANG_LABELS,
      colorPalette: COLOR_PALETTE,
      defaultColor: DEFAULT_COLOR,
      codeMaxLength: CODE_MAX_LENGTH,
      originalCode: "",
      form: this.buildForm(null),
      rules: {
        "title.uz_lat": [{ required: true, message: "Majburiy maydon", trigger: "blur" }],
        code: [
          { required: true, message: "Kodni kiriting", trigger: "blur" },
          {
            validator: (rule, value, callback) => {
              if (value && !CODE_PATTERN.test(value)) {
                callback(new Error("Faqat kichik lotin harflar, raqam, _ va - (32 belgigacha)"));
                return;
              }
              callback();
            },
            trigger: "blur",
          },
        ],
      },
    };
  },
  computed: {
    isEdit() {
      return !!this.periodicity;
    },
    previewTitle() {
      return displayLangText(this.form.title) || "Davriylik nomi";
    },
    badgeTextColor() {
      return contrastTextColor(this.form.color || DEFAULT_COLOR);
    },
  },
  watch: {
    modelValue(val) {
      if (!val) return;
      this.activeLang = "uz_lat";
      this.langErrors = [];
      this.form = this.buildForm(this.periodicity);
      this.originalCode = this.periodicity?.code || "";
    },
  },
  methods: {
    buildForm(periodicity) {
      return {
        code: periodicity?.code || "",
        title: fillMultiLang(periodicity?.title),
        color: periodicity?.color || COLOR_PALETTE[this.count % COLOR_PALETTE.length],
        order_number: periodicity?.order_number ?? this.nextOrder,
        is_active: periodicity?.is_active !== false,
      };
    },
    isSelectedColor(color) {
      return normalizeHex(this.form.color) === normalizeHex(color);
    },
    clearLangError(code) {
      this.langErrors = this.langErrors.filter((c) => c !== code);
    },
    onSubmit() {
      this.langErrors = [];
      this.$refs.formRef.validate((valid, invalidFields) => {
        if (!valid) {
          ElMessage.error("Iltimos, majburiy maydonlarni to'ldiring");
          if (invalidFields) {
            this.langErrors = Object.keys(invalidFields)
                .filter((key) => key.startsWith("title."))
                .map((key) => key.split(".")[1]);
            if (this.langErrors[0]) this.activeLang = this.langErrors[0];
          }
          return;
        }
        // PUT to'liq almashtirish — hamma maydon yuboriladi (JAVOBLAR.md A3).
        const payload = {
          code: this.form.code.trim(),
          title: { ...this.form.title },
          color: this.form.color || DEFAULT_COLOR,
          order_number: Number(this.form.order_number) || 0,
          is_active: this.form.is_active,
        };
        this.submitting = true;
        const request = this.isEdit
            ? api.updatePeriodicity(this.periodicity.id, payload)
            : api.createPeriodicity(payload);
        request
            .then(() => {
              ElMessage({
                type: "success",
                message: this.isEdit ? "Davriylik yangilandi" : "Davriylik yaratildi",
              });
              this.$emit("update:modelValue", false);
              this.$emit("saved");
            })
            .catch((error) => {
              ElNotification.error({
                title: "Saqlashda xatolik",
                message: apiErrorMessage(error),
              });
            })
            .finally(() => {
              this.submitting = false;
            });
      });
    },
  },
};
</script>

<style scoped lang="scss">
@use "./gfs-calendar.scss";
</style>
